/**
 * Setlist calculator utility
 *
 * Computes where the "Set End" divider should appear in a ranked song list,
 * and how much time remains in the current set.
 *
 * All durations are in SECONDS unless otherwise noted.
 */

/**
 * Compute the index in the ranked song list after which the set time is exhausted.
 *
 * The "up next" song (if any) is counted first (it WILL be played), then songs
 * from the ranked list are accumulated until the budget runs out.
 *
 * @param {Array}  rankedSongs       – sorted results array (excluding upNext & performed)
 * @param {Object} options
 * @param {number} options.setDurationMinutes   – duration of the current set in minutes
 * @param {number} options.pauseTimeSec         – seconds of pause between songs
 * @param {number} options.defaultSongTimeSec   – fallback song length in seconds
 * @param {Object|null} options.upNextSong      – the locked "up next" song (or null)
 * @param {Object|null} options.currentSong     – the currently playing song (or null)
 * @param {string|null} options.setStartedAt    – ISO timestamp when the set started
 * @param {Object}      options.songLengthMap   – { songId: lengthInSeconds } lookup
 * @returns {number} index in rankedSongs after which the set ends (-1 if all fit, or no set active)
 */
export function computeSetEndIndex(rankedSongs, options = {}) {
  const {
    setDurationMinutes = 0,
    pauseTimeSec = 30,
    defaultSongTimeSec = 240,
    upNextSong = null,
    setStartedAt = null,
    songLengthMap = {}
  } = options;

  if (!setDurationMinutes || setDurationMinutes <= 0 || !setStartedAt) return -1;

  const totalBudgetSec = setDurationMinutes * 60;
  const now = Date.now();
  const elapsed = (now - new Date(setStartedAt).getTime()) / 1000;

  // Remaining budget from now
  let remaining = totalBudgetSec - elapsed;
  if (remaining <= 0) return -1; // set already over – hide the divider

  const getSongDuration = (song) => {
    if (!song) return defaultSongTimeSec;
    const id = song.id || song.songId;
    const len = song.songLength || songLengthMap[id] || null;
    return len && len > 0 ? len : defaultSongTimeSec;
  };

  // Deduct the currently playing song's remaining time (estimate: full length from start)
  // We don't deduct it because elapsed time already accounts for it.

  // Deduct the up-next song (it will definitely be played next)
  if (upNextSong) {
    remaining -= getSongDuration(upNextSong) + pauseTimeSec;
    if (remaining <= 0) return 0;
  }

  // Walk through the ranked list
  for (let i = 0; i < rankedSongs.length; i++) {
    const song = rankedSongs[i];
    const cost = getSongDuration(song) + pauseTimeSec;
    remaining -= cost;
    if (remaining <= 0) {
      return i; // this song is the last that fits (barely)
    }
  }

  return -1; // all songs fit within the set
}

/**
 * Compute remaining seconds in the current set.
 *
 * @param {number} setDurationMinutes
 * @param {string|null} setStartedAt – ISO timestamp
 * @returns {number} seconds remaining (can be negative if over)
 */
export function computeRemainingSetTime(setDurationMinutes, setStartedAt) {
  if (!setDurationMinutes || !setStartedAt) return -1;
  const totalSec = setDurationMinutes * 60;
  const elapsed = (Date.now() - new Date(setStartedAt).getTime()) / 1000;
  return Math.round(totalSec - elapsed);
}

/**
 * Format seconds into MM:SS string.
 */
export function formatCountdown(totalSeconds) {
  if (totalSeconds < 0) return "0:00";
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.floor(totalSeconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Build a songLength lookup map from vote results or songs array.
 * @param {Array} songs – array of song objects with .id and .songLength
 * @returns {Object} { songId: lengthInSeconds }
 */
export function buildSongLengthMap(songs = []) {
  const map = {};
  songs.forEach((s) => {
    const id = s.id || s.songId || s.song?.id;
    const len = s.songLength || s.song?.songLength || null;
    if (id && len) map[id] = len;
  });
  return map;
}
